if (!customElements.get('cocon-product-gallery')) {
  customElements.define('cocon-product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('.cdp-gallery__track');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbnails = [...this.querySelectorAll('[data-thumbnail]')];
      this.dialog = this.querySelector('dialog');
      this.index = 0;
      if (!this.slides.length) return;
      this.querySelector('[data-gallery-controls]').hidden = false;
      this.querySelector('[data-thumbnails]').hidden = false;
      this.querySelector('[data-previous]').addEventListener('click', () => this.go(this.index - 1), options);
      this.querySelector('[data-next]').addEventListener('click', () => this.go(this.index + 1), options);
      this.thumbnails.forEach((button, index) => button.addEventListener('click', () => this.go(index), options));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.update(Math.min(this.slides.length - 1, Math.max(0, index)));
        });
      }, options);
      this.track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.querySelector('[data-zoom]').addEventListener('click', () => {
        const content = this.slides[this.index].firstElementChild.cloneNode(true);
        if (content.tagName === 'IMG') content.loading = 'eager';
        this.querySelector('[data-zoom-content]').replaceChildren(content);
        this.dialog.showModal();
      }, options);
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dialog.close();
        }
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: 'instant' });
      });
      this.resizeObserver.observe(this.track);
      this.update(0);
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          const slide = event.target.closest('[data-slide]');
          const index = this.slides.indexOf(slide);
          if (index >= 0) this.go(index);
        }, options);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      if (this.dialog?.open) this.dialog.close();
    }

    go(index) {
      const next = Math.min(this.slides.length - 1, Math.max(0, index));
      this.update(next);
      this.track.scrollTo({
        left: next * this.track.clientWidth,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }

    update(index) {
      this.index = index;
      this.querySelector('[data-previous]').disabled = index === 0;
      this.querySelector('[data-next]').disabled = index === this.slides.length - 1;
      this.querySelector('[data-gallery-status]').textContent = `${index + 1} / ${this.slides.length}`;
      this.thumbnails.forEach((button, i) => {
        if (i === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }
  });
}

if (!customElements.get('cocon-product-purchase')) {
  customElements.define('cocon-product-purchase', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.form = this.querySelector('form');
      this.button = this.form.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-purchase-error]');
      this.status = this.querySelector('[data-purchase-status]');
      this.form.addEventListener('submit', (event) => this.submit(event), { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || this.button.disabled || !this.form.reportValidity()) return;
      this.busy = true;
      this.error.hidden = true;
      this.status.hidden = true;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-busy', 'true');
      this.querySelector('.loading__spinner').classList.remove('hidden');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const data = new FormData(this.form);
      if (cart) {
        data.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
        data.append('sections_url', location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          this.error.textContent = result.description || result.message || (typeof result.errors === 'string' ? result.errors : this.dataset.error);
          this.error.hidden = false;
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cocon-product-purchase',
            productVariantId: data.get('id'),
            errors: this.error.textContent,
            message: result.message,
          });
          return;
        }
        if (!result.id && !result.items?.length) throw new Error('Invalid cart add response');
        this.status.textContent = this.dataset.success;
        this.status.hidden = false;
        if (cart) {
          cart.renderContents(result);
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cocon-product-purchase',
            productVariantId: data.get('id'),
            cartData: result,
          });
        } else {
          location.assign(window.routes.cart_url);
        }
      } catch (error) {
        console.error('Cocon product cart:', error);
        this.error.textContent = this.status.hidden ? this.dataset.error : this.dataset.cartError;
        this.error.hidden = false;
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-disabled');
        this.button.removeAttribute('aria-busy');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }
    }
  });
}
